import { HeaderMedia } from "../../../../resources/modelTypes";

/**
 * A browser-loadable URL for a header placement. Prefers original over the
 * 400px preview; both are absolute, unlike `image`'s storage path. For a video
 * this is only the thumbnail; HeaderMedia.tsx builds playable URLs.
 */
export const resolveHeaderMediaUrl = (
  headerMediaItem: HeaderMedia,
): string | null => headerMediaItem.original || headerMediaItem.preview || null;

/**
 * Drops video placements. Every layout but Landscape and FullBleed renders only
 * <img>, so those must run this before any position lookup.
 */
export const filterToPhotoOnlyHeaderMedia = (
  headerMedia: HeaderMedia[],
): HeaderMedia[] =>
  headerMedia.filter(
    (headerMediaItem) => headerMediaItem.mediaType !== "video",
  );

/** Header media in display order (ascending position). */
export const getOrderedHeaderMedia = (
  headerMedia: HeaderMedia[],
): HeaderMedia[] =>
  [...headerMedia].sort((left, right) => left.position - right.position);

/** Header media at a fixed modular slot (position), if any. */
export const getHeaderMediaAtPosition = (
  headerMedia: HeaderMedia[],
  position: number,
): HeaderMedia | undefined =>
  headerMedia.find((headerMediaItem) => headerMediaItem.position === position);

export type HeaderMediaFrame = {
  position: number;
  url: string;
};

/** Header media as display frames, preserving each placement's position. */
export const getHeaderMediaFrames = (
  headerMedia: HeaderMedia[],
): HeaderMediaFrame[] =>
  getOrderedHeaderMedia(headerMedia).flatMap((headerMediaItem) => {
    const url = resolveHeaderMediaUrl(headerMediaItem);

    return url ? [{ position: headerMediaItem.position, url }] : [];
  });

/** Slot index of the primary header media (lowest position), or 0 when empty. */
export const getPrimaryHeaderSlotPosition = (
  headerMedia: HeaderMedia[],
): number => {
  const ordered = getOrderedHeaderMedia(headerMedia);

  return ordered.length > 0 ? ordered[0].position : 0;
};

/** Resolves the URL for the primary (first-by-position) header media. */
export const getPrimaryHeaderMediaUrl = (
  headerMedia: HeaderMedia[],
): string | null => {
  const ordered = getOrderedHeaderMedia(headerMedia);

  if (ordered.length === 0) {
    return null;
  }

  return resolveHeaderMediaUrl(ordered[0]);
};
